
import { createClient } from '@supabase/supabase-js';

/**
 * Robust environment variable resolver.
 * Standardizes access across Vite, Vercel, and other hosting platforms.
 */
const getEnv = (key: string): string => {
  const win = window as any;
  
  // Try common naming conventions for the same secret
  const variations = [
    `VITE_PUBLIC_${key}`,
    `VITE_${key}`,
    `NEXT_PUBLIC_${key}`,
    key
  ];

  for (const v of variations) {
    // 1. Check import.meta.env (Vite/Modern ESM)
    try {
      // @ts-ignore
      if (typeof import.meta !== 'undefined' && import.meta.env?.[v]) {
        // @ts-ignore
        return import.meta.env[v];
      }
    } catch (e) {}

    // 2. Check process.env (Standard Node/Webpack)
    try {
      if (typeof process !== 'undefined' && process.env?.[v]) {
        return process.env[v] as string;
      }
    } catch (e) {}

    // 3. Check window.process.env (Platform Shim)
    try {
      if (win.process?.env?.[v]) {
        return win.process.env[v];
      }
    } catch (e) {}
  }

  return '';
};

const SUPABASE_URL = getEnv('SUPABASE_URL');
const SUPABASE_ANON_KEY = getEnv('SUPABASE_ANON_KEY') || getEnv('SUPABASE_PUBLISHABLE_DEFAULT_KEY');
const SUPABASE_SERVICE_ROLE_KEY = getEnv('SUPABASE_SERVICE_ROLE_KEY');

// Ensure we don't pass empty strings to createClient which causes the 'required' error
const finalUrl = SUPABASE_URL && SUPABASE_URL.startsWith('http') 
  ? SUPABASE_URL 
  : 'https://placeholder-project.supabase.co';

const finalKey = SUPABASE_ANON_KEY || 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.placeholder';

const isValidConfig = SUPABASE_URL && SUPABASE_ANON_KEY && SUPABASE_URL.startsWith('http');

if (!isValidConfig) {
  console.warn("⚠️ Supabase: Configuration missing or invalid. Auth and Database features will not work.");
  console.warn("💡 Ensure VITE_PUBLIC_SUPABASE_URL and VITE_PUBLIC_SUPABASE_ANON_KEY are set in your environment.");
}

export const supabase = createClient(finalUrl, finalKey);

/**
 * Creates a Supabase client with the Service Role key.
 * Used ONLY in backend API routes (like webhooks) to bypass RLS.
 */
export const getSupabaseAdmin = () => {
  if (!SUPABASE_URL || !SUPABASE_SERVICE_ROLE_KEY) {
    throw new Error("Missing Supabase Admin configuration (URL or Service Role Key).");
  }
  return createClient(SUPABASE_URL, SUPABASE_SERVICE_ROLE_KEY);
};

export const getUser = async () => {
  if (!isValidConfig) return null;
  const { data: { user } } = await supabase.auth.getUser();
  return user;
};

export const signOut = async () => {
  try {
    const { error } = await supabase.auth.signOut();
    if (error) {
      console.error('Error during sign out:', error);
    } else {
      console.log('User signed out successfully');
    }
  } catch (error) {
    console.error('Unexpected error during sign out:', error);
  }
};
